import React, { useState } from 'react';
import axios from 'axios';
import {
    ChartBarIcon,
    ArrowDownTrayIcon,
    DocumentChartBarIcon,
    PresentationChartLineIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import i18n from '../i18n';

const downloadPDF = async (type, title) => {
    try {
        let endpoint = '/api/v1/reporting/export/compliance';
        if (type === 'MARKET') endpoint = '/api/v1/reporting/export/market-volume';
        if (type === 'ACCOUNT') endpoint = '/api/v1/reporting/export/account-statement';
        if (type === 'DISCLOSURE') endpoint = '/api/v1/reporting/export/disclosure-proof';

        const response = await axios.get(endpoint, {
            responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
    } catch (err) {
        console.error("Export failed:", err);
        alert(i18n.t('export_failed'));
    }
};

const ReportCard = ({ title, desc, icon: Icon, type }) => {
    return (
        <div className="glass-card p-8 rounded-none border-gray-200 bg-white flex flex-col items-center text-center group cursor-pointer hover:bg-blue-50/20 transition-all border shadow-sm">
            <div className="w-16 h-16 bg-blue-50 rounded flex items-center justify-center text-[#155e75] mb-6 group-hover:scale-110 transition-transform shadow-inner">
                <Icon className="w-8 h-8" />
            </div>
            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-tight mb-2">{title}</h3>
            <p className="text-xs text-gray-500 leading-relaxed mb-6 h-12 font-medium">{desc}</p>
            <button
                onClick={() => downloadPDF(type, title)}
                className="flex items-center gap-2 text-[10px] font-black text-[#155e75] uppercase tracking-widest hover:underline bg-gray-50 px-4 py-2 rounded-sm border border-gray-100"
            >
                <ArrowDownTrayIcon className="w-4 h-4" /> {i18n.t('download_pdf')}
            </button>
        </div>
    );
};

export default function Reports() {
    const [wizardOpen, setWizardOpen] = useState(false);
    const [dates, setDates] = useState({ start: '', end: '' });

    const handleRunWizard = async () => {
        try {
            const response = await axios.get(`/api/v1/reporting/export/custom-wizard?start_date=${dates.start}&end_date=${dates.end}`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Custom_Audit_Manifest.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            setWizardOpen(false);
        } catch (err) {
            alert(i18n.t('wizard_failed'));
        }
    };

    return (
        <div className="space-y-8 animate-fade-in px-4 relative">
            <div className="border-b border-gray-200 pb-4">
                <h1 className="text-3xl font-bold text-gray-800 tracking-tight">{i18n.t('compliance_reporting')}</h1>
                <p className="text-sm text-gray-500 font-medium">{i18n.t('compliance_reporting_desc')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <ReportCard
                    title={i18n.t('market_volume_report')}
                    type="MARKET"
                    desc={i18n.t('market_volume_desc')}
                    icon={ChartBarIcon}
                />
                <ReportCard
                    title={i18n.t('account_statement')}
                    type="ACCOUNT"
                    desc={i18n.t('account_statement_desc')}
                    icon={DocumentChartBarIcon}
                />
                <ReportCard
                    title={i18n.t('disclosure_proof')}
                    type="DISCLOSURE"
                    desc={i18n.t('disclosure_proof_desc')}
                    icon={PresentationChartLineIcon}
                />
            </div>

            <div className="p-10 bg-blue-50 border border-blue-100 rounded-none flex flex-col md:flex-row items-center justify-between shadow-inner gap-6 overflow-hidden relative">
                <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                    <PresentationChartLineIcon className="w-40 h-40" />
                </div>
                <div className="flex items-center gap-6 relative z-10">
                    <div className="p-4 bg-[#155e75] text-white rounded-sm shadow-md">
                        <ChartBarIcon className="w-8 h-8" />
                    </div>
                    <div>
                        <p className="text-lg font-bold text-gray-800 uppercase tracking-tight">{i18n.t('custom_audit_manifest')}</p>
                        <p className="text-sm text-gray-500 font-medium mt-1">{i18n.t('custom_audit_desc')}</p>
                    </div>
                </div>
                <button
                    onClick={() => setWizardOpen(true)}
                    className="btn-grexel text-[11px] uppercase tracking-widest px-12 py-4 shadow-lg relative z-10"
                >
                    {i18n.t('init_wizard')}
                </button>
            </div>

            {wizardOpen && (
                <div className="fixed inset-0 bg-[#0d5466]/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-none border border-gray-200 shadow-2xl w-full max-w-md animate-slide-up">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <h2 className="text-sm font-black text-[#155e75] uppercase tracking-widest">{i18n.t('wizard_title')}</h2>
                            <button onClick={() => setWizardOpen(false)}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
                        </div>
                        <div className="p-8 space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{i18n.t('start_date')}</label>
                                <input type="date" value={dates.start} onChange={e => setDates({ ...dates, start: e.target.value })} className="full-input" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{i18n.t('end_date')}</label>
                                <input type="date" value={dates.end} onChange={e => setDates({ ...dates, end: e.target.value })} className="full-input" />
                            </div>
                            <button onClick={handleRunWizard} className="btn-grexel w-full py-4 text-[11px] uppercase tracking-widest">{i18n.t('generate_extract')}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
